import { Injectable } from '@angular/core';

const THEME_COOKIE = 'pureCloudTheme';
const TEN_YEARS = 315360000; // in seconds

enum ThemeClasses {
  ORIGINAL = 'original-theme',
  LIGHT = 'light-theme',
  DARK = 'dark-theme',
  BASE = 'purecloud-theme'
}

const THEME_LIST = ['light-theme', 'dark-theme'];

// Needed for continued support of original blue theme.
// Once support for this is cleaned up this property can be removed
const SUPPORTED_THEMES = ['original-theme', ...THEME_LIST];

const DEFAULT_THEME = ThemeClasses.LIGHT;

/** ? */
class Theme {
  public theme: string = null;
  constructor(theme: string = DEFAULT_THEME) {
    this.theme = theme;
  }
}

/** ? */
@Injectable({
  providedIn: 'root'
})
export class ThemeLoaderService {
  public currentTheme = null;

  public loadCachedTheme(cookies: string = document.cookie): Theme {
    let cookie = cookies
      .split(/;[ \t]?/)
      .map((item) => {
        let [name, theme] = item.split('=');

        return { name, theme };
      })
      .find(({ name }) => name === THEME_COOKIE);

    if (cookie && SUPPORTED_THEMES.find((theme) => theme === cookie.theme)) {
      return new Theme(cookie.theme);
    }

    return new Theme(ThemeClasses.ORIGINAL);
  }

  public getThemeClass(): any {
    return this.currentTheme;
  }

  public setThemeClass({
    theme = new Theme(ThemeClasses.LIGHT),
    element = document.body
  }: any): void {
    if (this.currentTheme) {
      element.classList.remove(this.currentTheme);
    }

    element.classList.add(ThemeClasses.BASE);
    element.classList.add(theme.theme); // IE11 - must add classes separately - element.classList.add(class1, class2) not supported (PCADMIN-5818)
    this.currentTheme = theme.theme;
  }

  public validateThemeAgainstFeatureToggles(
    genesysUIBranding: any,
    element: HTMLElement = document.body
  ): void {
    if (genesysUIBranding && this.currentTheme === ThemeClasses.ORIGINAL) {
      this.setThemeClass({ theme: new Theme(DEFAULT_THEME), element });
      this.setThemeCookie({ theme: DEFAULT_THEME });
    } else if (!genesysUIBranding && this.currentTheme !== ThemeClasses.ORIGINAL) {
      this.setThemeClass({ theme: new Theme(ThemeClasses.ORIGINAL), element });
      this.setThemeCookie({ theme: '', duration: 0 });
    }
  }

  private setThemeCookie({
    theme = ThemeClasses.LIGHT,
    duration = TEN_YEARS,
    path = '/',
    domain = this.getEnvCookieDomain()
  }: IThemeLoaderSetThemeCookieOptions = {}): void {
    this.setCookie(`${THEME_COOKIE}=${theme};path=${path};domain=${domain};max-age=${duration}`);
  }

  private getEnvCookieDomain(): string {
    if (location.hostname === 'localhost') {
      return 'localhost';
    }
    let ref = location.hostname.match(/^.*((?:inin[dts]ca|mypurecloud)\S*)\s*$/i);

    // eslint-disable-next-line no-void
    return `.${ref != null ? ref[1] : void 0}`;
  }

  private resetModule(): void {
    this.currentTheme = null;
  }

  private setCookie(cookie: string): void {
    document.cookie = cookie;
  }
}

interface IThemeLoaderSetThemeCookieOptions {
  theme?: string;
  duration?: number;
  path?: string;
  domain?: string;
}
